import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, RotateCcw, Award, Download, CheckCircle2, XCircle, Target, BookOpen, ChevronDown } from "lucide-react";
import Az900Certificate from "@/components/az900/Az900Certificate";

function fmtDuration(sec) {
  if (sec == null) return "—";
  const m = Math.floor(sec / 60);
  return `${m}m ${sec % 60}s`;
}

export default function Az900Result({ result, onBack, onRetake }) {
  const [filter, setFilter] = useState("all"); // all | wrong | flagged
  const [open, setOpen] = useState({});
  const [cert, setCert] = useState(null);
  const [certOpen, setCertOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const certRef = useRef(null);

  const passed = result.passed;
  const pct = Math.round((result.scaled_score / 1000) * 100);
  const benchPct = Math.round((result.benchmark / 1000) * 100);

  const review = result.review.filter((r) => {
    if (filter === "wrong") return !r.is_correct;
    if (filter === "flagged") return false; // flags not in result; keep simple
    return true;
  });

  const openCert = async () => {
    try {
      const r = await api.get(`/az900/certificate/${result.attempt_id}`);
      setCert(r.data);
      setCertOpen(true);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const downloadCert = async () => {
    if (!certRef.current) return;
    setDownloading(true);
    try {
      let url;
      try { url = await toPng(certRef.current, { pixelRatio: 2, cacheBust: true }); }
      catch { url = await toPng(certRef.current, { pixelRatio: 2, cacheBust: true, skipFonts: true }); }
      const link = document.createElement("a");
      link.download = `${cert.certificate_id}.png`;
      link.href = url;
      link.click();
    } catch {
      toast.error("Could not generate certificate image.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="az900-result">
      <Button variant="ghost" onClick={onBack} data-testid="az900-result-back" className="gap-2 text-muted-foreground -ml-2 mb-4">
        <ArrowLeft className="h-4 w-4" /> Back to simulator
      </Button>

      {/* Score summary */}
      <div className={`rounded-2xl border p-7 ${passed ? "border-emerald-500/30 bg-emerald-500/5" : "border-rose-500/30 bg-rose-500/5"}`}>
        <div className="flex flex-col md:flex-row items-center gap-8">
          {/* Gauge */}
          <div className="relative h-40 w-40 shrink-0" data-testid="az900-score-gauge">
            <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
              <circle cx="60" cy="60" r="52" fill="none" stroke="currentColor" strokeWidth="10" className="text-muted" />
              <circle
                cx="60" cy="60" r="52" fill="none" strokeWidth="10" strokeLinecap="round"
                className={passed ? "text-emerald-500" : "text-rose-500"} stroke="currentColor"
                strokeDasharray={`${(pct / 100) * 326.7} 326.7`}
              />
            </svg>
            <div className="absolute inset-0 grid place-items-center">
              <div className="text-center">
                <div className="text-4xl font-extrabold font-mono">{result.scaled_score}</div>
                <div className="text-[11px] text-muted-foreground">of 1000</div>
              </div>
            </div>
          </div>

          <div className="flex-1 text-center md:text-left">
            <div className={`inline-flex items-center gap-2 text-sm font-bold px-3 py-1.5 rounded-full ${passed ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "bg-rose-500/15 text-rose-600 dark:text-rose-400"}`} data-testid="az900-result-badge">
              {passed ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
              {passed ? "Benchmark met" : "Below benchmark"}
            </div>
            <h1 className="font-display font-extrabold text-2xl mt-3 tracking-tight">
              You scored {result.correct} of {result.total} correct
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Benchmark to pass is <span className="font-semibold text-foreground">{result.benchmark}</span> · Completed in {fmtDuration(result.duration_sec)}
            </p>

            {/* Benchmark bar */}
            <div className="mt-4 relative h-3 rounded-full bg-muted overflow-hidden">
              <div className={`h-full rounded-full ${passed ? "bg-emerald-500" : "bg-rose-500"}`} style={{ width: `${pct}%` }} />
              <div className="absolute top-0 bottom-0 w-0.5 bg-foreground/70" style={{ left: `${benchPct}%` }} title={`Benchmark ${result.benchmark}`} />
            </div>

            {result.mode === "full" && (
              <Button onClick={openCert} data-testid="az900-cert-open" className="mt-5 gap-2 bg-[#0078d4] hover:bg-[#106ebe]">
                <Award className="h-4 w-4" /> View certificate
              </Button>
            )}
            <Button onClick={onRetake} variant="outline" data-testid="az900-retake" className="mt-5 ml-3 gap-2">
              <RotateCcw className="h-4 w-4" /> Retake
            </Button>
          </div>
        </div>
      </div>

      {/* Domain breakdown */}
      <div className="mt-8 grid md:grid-cols-2 gap-6">
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 font-semibold"><Target className="h-4 w-4 text-[#0078d4]" /> Performance by domain</div>
          <div className="mt-4 space-y-4" data-testid="az900-domains">
            {result.domains.map((d) => (
              <div key={d.id}>
                <div className="flex justify-between text-sm">
                  <span>{d.name}</span>
                  <span className="font-mono text-muted-foreground">{d.correct}/{d.total} · {d.pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-muted mt-1.5 overflow-hidden">
                  <div className={`h-full rounded-full ${d.pct >= 70 ? "bg-emerald-500" : d.pct >= 50 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${d.pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 font-semibold"><BookOpen className="h-4 w-4 text-[#0078d4]" /> Areas to focus on</div>
          {result.weak_objectives.length === 0 ? (
            <p className="text-sm text-muted-foreground mt-4" data-testid="az900-weak-none">Great balance across objectives — no clear weak spots.</p>
          ) : (
            <ul className="mt-4 space-y-2" data-testid="az900-weak">
              {result.weak_objectives.map((w) => (
                <li key={w} className="flex items-center gap-2 text-sm">
                  <span className="h-1.5 w-1.5 rounded-full bg-rose-500" /> {w}
                </li>
              ))}
            </ul>
          )}
          <div className="mt-5 border-t border-border pt-4">
            <div className="text-xs font-medium text-muted-foreground mb-2">By objective</div>
            <div className="space-y-1.5 max-h-40 overflow-auto pr-1">
              {result.objectives.map((o) => (
                <div key={o.objective} className="flex justify-between text-xs">
                  <span className="truncate pr-2">{o.objective}</span>
                  <span className={`font-mono ${o.pct >= 70 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>{o.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Review */}
      <div className="mt-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-display font-bold text-xl tracking-tight">Answer review</h2>
          <div className="flex gap-2">
            {[["all", "All"], ["wrong", "Incorrect only"]].map(([k, label]) => (
              <Button key={k} size="sm" variant={filter === k ? "default" : "outline"} onClick={() => setFilter(k)} data-testid={`az900-filter-${k}`} className={filter === k ? "bg-[#0078d4] hover:bg-[#106ebe]" : ""}>
                {label}
              </Button>
            ))}
          </div>
        </div>

        <div className="mt-4 space-y-3" data-testid="az900-review-list">
          {review.map((r, i) => {
            const isOpen = open[r.qid];
            return (
              <div key={r.qid} className={`rounded-xl border ${r.is_correct ? "border-emerald-500/30" : "border-rose-500/30"} bg-card overflow-hidden`} data-testid={`az900-review-${r.qid}`}>
                <button onClick={() => setOpen((p) => ({ ...p, [r.qid]: !p[r.qid] }))} className="w-full flex items-start gap-3 p-4 text-left hover:bg-accent/50">
                  {r.is_correct ? <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" /> : <XCircle className="h-5 w-5 text-rose-500 shrink-0 mt-0.5" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] text-muted-foreground">{r.domain_name} · {r.objective}{!r.answered && " · unanswered"}</div>
                    <div className="text-sm font-medium mt-0.5">{r.prompt}</div>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 pl-12 space-y-2">
                    {r.options.map((opt, oi) => {
                      const correct = r.correct_indices.includes(oi);
                      const chosen = r.your_indices.includes(oi);
                      return (
                        <div key={oi} className={`text-sm px-3 py-2 rounded-md border flex items-center gap-2 ${
                          correct ? "border-emerald-500/40 bg-emerald-500/10" : chosen ? "border-rose-500/40 bg-rose-500/10" : "border-border"
                        }`}>
                          {correct && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />}
                          {chosen && !correct && <XCircle className="h-3.5 w-3.5 text-rose-500" />}
                          <span>{opt}</span>
                          {chosen && <span className="ml-auto text-[10px] uppercase tracking-wide text-muted-foreground">Your answer</span>}
                        </div>
                      );
                    })}
                    <div className="text-sm text-muted-foreground bg-muted/50 rounded-md p-3 mt-2">
                      <span className="font-semibold text-foreground">Explanation: </span>{r.explanation}
                      {r.ref && (
                        <a href={r.ref} target="_blank" rel="noreferrer" className="block mt-2 text-[#0078d4] hover:underline text-xs">
                          Microsoft Learn reference →
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Certificate dialog */}
      <Dialog open={certOpen} onOpenChange={setCertOpen}>
        <DialogContent className="max-w-3xl" data-testid="az900-cert-dialog">
          <DialogHeader>
            <DialogTitle>AZ-900 Simulation Certificate</DialogTitle>
          </DialogHeader>
          {cert && (
            <div className="overflow-hidden rounded-lg border border-border" style={{ height: 707 * 0.62 }}>
              <div style={{ transform: "scale(0.62)", transformOrigin: "top left", width: 1000, height: 707 }}>
                <Az900Certificate ref={certRef} cert={cert} />
              </div>
            </div>
          )}
          <div className="flex justify-end">
            <Button onClick={downloadCert} disabled={downloading} data-testid="az900-cert-download" className="gap-2 bg-[#0078d4] hover:bg-[#106ebe]">
              <Download className="h-4 w-4" /> {downloading ? "Generating…" : "Download PNG"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
